"use client";

import * as React from "react";
import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // BASE: Revertido para o estilo antigo (flex, focus rings padrões, radius-md)
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          // MANTIDO: Sua customização de dark mode
          "bg-primary text-primary-foreground shadow hover:bg-primary/90 dark:text-white",

        destructive:
          // REVERTIDO: Volta a ser vermelho sólido (o novo era transparente com texto vermelho)
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",

        outline:
          // REVERTIDO: Borda padrão input e background
          "border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",

        secondary:
          // REVERTIDO: Estilo secundário padrão
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",

        ghost: "text-foreground hover:bg-accent hover:text-accent-foreground",

        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // REVERTIDO: Tamanhos originais (h-9 padrão contra h-8 do novo formato)
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        xs: "h-7 rounded-md px-2 text-xs",
        "icon-xs":
          "in-data-[slot=button-group]:rounded-lg size-6 rounded-[min(var(--radius-md),10px)] [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "in-data-[slot=button-group]:rounded-lg size-7 rounded-[min(var(--radius-md),12px)]",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonProps = React.ComponentProps<typeof ButtonPrimitive> &
  VariantProps<typeof buttonVariants>;

// forwardRef é obrigatório: sem ele os `Trigger asChild` do Radix (Popover/Tooltip/DropdownMenu)
// não recebem a âncora e falham em silêncio — o painel não abre nem posiciona.
const Button = React.forwardRef<
  HTMLElement,
  ButtonPrimitive.Props & VariantProps<typeof buttonVariants>
>(({ className, variant, size, ...props }, ref) => (
  <ButtonPrimitive
    ref={ref}
    data-slot="button"
    className={cn(buttonVariants({ variant, size, className }))}
    {...props}
  />
));
Button.displayName = "Button";

export { Button, buttonVariants };
